<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-dropdown right
                          no-caret
                          variant="link"
                          boundary="window"
                          toggle-class="list-view-icon-btn list-view-icon-btn--primary"
                          v-b-tooltip.hover.bottom
                          title="Opciones"
                          v-if="grid.rowData.length || isCreateMode">
                <template #button-content>
                  <AppIcon name="tune" label="Opciones" />
                </template>
                <b-dropdown-item @click="addRow()">
                  <AppIcon name="add" /> Agregar item
                </b-dropdown-item>
                <b-dropdown-item @click="showBulkEdit = !showBulkEdit">
                  <AppIcon name="edit" /> Edición masiva
                </b-dropdown-item>
                <b-dropdown-item @click="removeFocusedRow()">
                  <AppIcon name="delete" /> Eliminar item
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </section>
      </b-card>

      <b-card v-if="!(isCreateMode && !form.suppliers_id)" body-class="py-2">
        <b-row class="align-items-center">
          <b-col md="7" class="d-flex align-items-center flex-wrap">
            <b class="mr-2">{{ header.supplier }}</b>
            <div v-if="header.category || header.subcategory" class="small text-muted w-100 mt-1">
              <span v-if="header.category">Categoría: {{ header.category }}</span>
              <span v-if="header.subcategory" class="ml-2">Subcategoría: {{ header.subcategory }}</span>
            </div>
          </b-col>
          <b-col md="5" class="cpls-mode-indicators">
            <div class="cpls-iva-toggle"
                 :class="{ 'cpls-iva-toggle--active': priceWithIva }"
                 v-b-tooltip.hover.bottom
                 title="Ingresar y visualizar el precio de lista con IVA incluido">
              <b-form-checkbox v-model="priceWithIva" switch>
                <span class="font-weight-bold">Precios c/IVA</span>
              </b-form-checkbox>
            </div>
            <div v-if="showRoundPriceWarning"
                 id="cie-round-price-badge"
                 class="cpls-rounded-badge cie-rounded-badge--clickable"
                 tabindex="0">
              <AppIcon name="info" class="mr-1" />Redondeo activo
            </div>
            <b-popover v-if="showRoundPriceWarning" target="cie-round-price-badge" triggers="hover focus click" placement="bottom">
              <template #title><AppIcon name="arrow_circle_up" class="mr-1" />Redondeo de precios activo</template>
              <div class="small cie-round-price-popover">
                La edición manual por precio final está deshabilitada porque el redondeo de precios está activo. Las columnas de venta se muestran solo como referencia.
              </div>
            </b-popover>
          </b-col>
        </b-row>
      </b-card>

      <div v-if="saving" class="app-alert app-alert--warning mb-3">
        <span class="app-alert__icon"><AppIcon name="history" /></span>
        <div class="app-alert__content">
          <div class="d-flex justify-content-between align-items-center mb-1 flex-wrap">
            <div class="font-weight-bold">Procesando importación...</div>
            <div class="small text-muted">
              <span v-if="importProgressRemainingSec > 0">Tiempo estimado restante: ~{{ formatDuration(importProgressRemainingSec) }}</span>
              <span v-else>Finalizando...</span>
            </div>
          </div>
          <b-progress :value="importProgressPercent" max="100" height="8px" variant="warning" animated></b-progress>
          <div class="small text-muted mt-1">
            {{ grid.rowData.length }} registros · {{ Math.max(1, listCount) }} listas
          </div>
        </div>
      </div>

      <b-card>
        <div v-if="!parameters.salePriceCalc" class="app-alert app-alert--warning mb-3" role="status">
          <span class="app-alert__icon"><AppIcon name="info" /></span>
          <div class="app-alert__content small">Para editar precios finales, activá el parámetro del sistema “Conservar precisión en precios de venta” en el módulo Costos. Hasta entonces, esas columnas son solo de consulta.</div>
        </div>
        <div v-if="hasImportValidationErrors" class="app-alert app-alert--danger mb-3">
          <span class="app-alert__icon"><AppIcon name="warning" /></span>
          <div class="app-alert__content">
            <div class="font-weight-bold mb-1">Se detectaron inconsistencias en los datos cargados</div>
            <div class="small mb-2">
              Corrija el origen antes de importar. Con este esquema el backend puede rechazar el archivo.
            </div>
            <div v-for="(message, index) in importValidation.errors" :key="'validation-error-' + index" class="small">
              <AppIcon name="chevron_right" class="mr-1" />{{ message }}
            </div>
          </div>
        </div>
        <div v-if="hasImportValidationWarnings" class="app-alert app-alert--warning mb-3">
          <span class="app-alert__icon"><AppIcon name="info" /></span>
          <div class="app-alert__content">
            <div class="font-weight-bold mb-1">Observaciones del archivo cargado</div>
            <div v-for="(message, index) in importValidation.warnings" :key="'validation-warning-' + index" class="small">
              <AppIcon name="chevron_right" class="mr-1" />{{ message }}
            </div>
          </div>
        </div>

        <EmptyState v-if="!grid.rowData.length && !isCreateMode"
                    icon="table_rows"
                    title="No hay datos cargados"
                    description='Use la opción de "Abrir en editor" desde la descarga del CSV en costos.' />

        <template v-else>
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap">
            <div class="d-flex align-items-center flex-wrap">
              <b-button
                variant="outline-primary"
                size="sm"
                class="mr-2 mb-2 d-inline-flex align-items-center"
                @click="downloadCSV()"
                :disabled="!grid.rowData.length"
                v-b-tooltip.hover
                title="Edite la planilla en su editor externo y vuelva a subirla desde el gestor de importaciones">
                <AppIcon name="download" class="mr-0 mr-sm-1" /><span class="d-none d-sm-inline">Descargar CSV</span>
              </b-button>
              <div class="small text-muted mb-2">
                {{ grid.rowData.length }} registros
              </div>
            </div>
            <div class="d-flex align-items-center mb-2">
              <b-button
                v-if="dirty"
                variant="outline-warning"
                size="sm"
                class="mr-2 d-inline-flex align-items-center"
                @click="resetToOriginal()"
                v-b-tooltip.hover
                title="Descartar cambios y volver a los datos originales">
                <AppIcon name="undo" class="mr-0 mr-sm-1" /><span class="d-none d-sm-inline">Restaurar original</span>
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="d-inline-flex align-items-center"
                @click="openColumnsModal()"
                v-b-tooltip.hover
                title="Configurar columnas visibles por defecto">
                <AppIcon name="view_column" class="mr-0 mr-sm-1" /><span class="d-none d-sm-inline">Columnas visibles</span>
              </b-button>
            </div>
          </div>

          <div v-if="isCreateMode && !grid.rowData.length" class="small text-muted mb-2">
            No hay filas cargadas. Use "Opciones → Agregar item" para comenzar.
          </div>

          <div ref="gridWrap" class="ag-theme-alpine my-grid-custom" :style="'width: 100%; height:' + gridHeight">
          <ag-grid-vue
            :style="'width:100%; height:' + gridHeight"
            class="ag-theme-alpine"
            :columnDefs="grid.columnDefs"
            :rowData="grid.rowData"
            :defaultColDef="defaultColDef"
            :sideBar="sideBar"
            :rowHeight="36"
            :stopEditingWhenCellsLoseFocus="true"
            :enableCellTextSelection="true"
            @grid-ready="onGridReady"
            @column-resized="onColumnResized"
            @cell-value-changed="onCellValueChanged">
          </ag-grid-vue>
          </div>
        </template>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="goBack()">
            <AppIcon name="back" />
            Volver
          </b-button>

          <b-button variant="dark" size="sm" class="d-inline-flex align-items-center" @click="openSaveModal()" :disabled="saving || !grid.rowData.length || hasImportValidationErrors">
            <AppIcon name="save" class="mr-1" /> Guardar
          </b-button>
        </div>
      </b-card>

      <b-modal v-model="showBulkEdit" size="md">
        <div slot="modal-header" class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="edit" /></span>
          <h5 class="app-modal-header__title">Edición masiva</h5>
        </div>

        <div class="small text-muted mb-3">Aplica el valor a todas las filas filtradas.</div>

        <b-form-group label="Columna">
          <b-form-select :options="bulkColumnsOptions" v-model="bulkEdit.column"></b-form-select>
        </b-form-group>
        <b-form-group label="Modo" v-if="isBulkPercentEligible">
          <b-form-select :options="bulkEdit.modeOptions" v-model="bulkEdit.mode"></b-form-select>
        </b-form-group>
        <b-form-group label="Modo" v-if="isBulkSalePriceColumn">
          <b-form-select :options="bulkEdit.salePriceModeOptions" v-model="bulkEdit.mode"></b-form-select>
        </b-form-group>
        <b-form-group label="Valor" v-if="isBulkIncludeColumn">
          <b-form-select :options="bulkEdit.includeOptions" v-model="bulkEdit.value"></b-form-select>
        </b-form-group>
        <b-form-group label="Múltiplo" v-if="isBulkSalePriceColumn && bulkEdit.mode === 'sale_multiple'">
          <b-form-select :options="bulkEdit.salePriceMultipleOptions" v-model="bulkEdit.salePriceMultiple"></b-form-select>
        </b-form-group>
        <b-form-group label="Valor" v-if="!isBulkIncludeColumn && (!isBulkSalePriceColumn || bulkEdit.mode !== 'sale_multiple')" class="mb-0">
          <b-form-input v-model="bulkEdit.value" :placeholder="bulkEditPlaceholder"></b-form-input>
        </b-form-group>

        <div v-if="isBulkPercentEligible" class="small text-muted mt-2">
          Ingrese porcentaje positivo o negativo para ajustar el valor actual.
        </div>
        <div v-if="isBulkSalePriceColumn && bulkEdit.mode === 'sale_percent'" class="small text-muted mt-2">
          Ingrese porcentaje positivo o negativo para ajustar el precio de venta. La utilidad se recalcula automáticamente.
        </div>
        <div v-if="isBulkSalePriceColumn && bulkEdit.mode === 'sale_multiple'" class="small text-muted mt-2">
          El precio de venta se redondea hacia arriba al múltiplo seleccionado. La utilidad se recalcula automáticamente.
        </div>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1 d-inline-flex align-items-center" size="sm" @click="showBulkEdit = false"><AppIcon name="x" class="mr-1" />Cancelar</b-button>
          <b-button variant="dark" size="sm" class="d-inline-flex align-items-center" @click="applyBulkEdit()" :disabled="!bulkEdit.column"><AppIcon name="check" class="mr-1" />Aplicar</b-button>
        </div>
      </b-modal>

      <b-modal v-model="modal.save.active"
               size="md">
        <div slot="modal-header" class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="save" /></span>
          <h5 class="app-modal-header__title">{{ modal.save.title }}</h5>
        </div>

        <b-row>
          <b-col md="12" class="mb-3">
            <b-form-group v-if="form.suppliers_id" label="Proveedor" class="mb-0">
              <b-form-input size="sm" :value="header.supplier" disabled></b-form-input>
            </b-form-group>
            <div v-else>
              <FindObject
                type="suppliers"
                @select-object="loadSuppliers($event)"
                :valueID="form.suppliers_id"
                :where="conditionSuppliersActive"
                render="search"/>
            </div>
            <div v-if="!form.suppliers_id" class="small text-muted mt-1">
              Seleccione el proveedor para asignar esta importación.
            </div>
          </b-col>
          <b-col md="12" class="mb-3">
            <b-form-group label="Tipo de importación" class="mb-0">
              <b-form-select :options="typeImportOptions" v-model="form.typeImport"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <div class="app-alert" :class="'app-alert--' + saveImportAlertVariant">
              <span class="app-alert__icon"><AppIcon :name="form.typeImport === 'delete' ? 'warning' : 'info'" /></span>
              <div class="app-alert__content small">
                {{ saveImportDescription }}
              </div>
            </div>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1 d-inline-flex align-items-center" size="sm" @click="modal.save.active=false"><AppIcon name="x" class="mr-1" />Cancelar</b-button>
          <b-button variant="dark" size="sm" class="d-inline-flex align-items-center" @click="confirmAndSave()" :disabled="!form.suppliers_id">
            <AppIcon name="save" class="mr-1" /> Confirmar e importar
          </b-button>
        </div>
      </b-modal>

      <b-modal v-model="modal.columns.active"
               size="md">
        <div slot="modal-header" class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="view_column" /></span>
          <h5 class="app-modal-header__title">{{ modal.columns.title }}</h5>
        </div>

        <b-row>
          <b-col md="12">
            <div class="small text-muted mb-2">
              Seleccione las columnas visibles. El archivo mantiene siempre el formato completo.
            </div>
            <div v-for="group in columnsVisibleGroups" :key="group.key" class="mb-3">
              <div class="small font-weight-bold text-uppercase text-muted mb-1">
                {{ group.title }}
              </div>

              <div v-if="group.type === 'general'" class="border rounded p-2">
                <b-form-checkbox
                  v-for="option in group.options"
                  :key="option.value"
                  v-model="columnsVisible.selected"
                  :value="option.value"
                  class="d-block mb-1">
                  {{ option.text }}
                </b-form-checkbox>
              </div>

              <div v-else class="border rounded p-2 bg-light">
                <div class="d-flex flex-wrap align-items-center">
                  <b-form-checkbox
                    v-for="option in group.options"
                    :key="option.value"
                    v-model="columnsVisible.selected"
                    :value="option.value"
                    class="mr-3 mb-1">
                    {{ option.text }}
                  </b-form-checkbox>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1 d-inline-flex align-items-center" size="sm" @click="modal.columns.active=false"><AppIcon name="x" class="mr-1" />Cancelar</b-button>
          <b-button variant="dark" size="sm" class="d-inline-flex align-items-center" @click="applyColumnVisibility()"><AppIcon name="check" class="mr-1" />Aplicar</b-button>
        </div>
      </b-modal>
    </b-col>
  </b-row>
</template>

<script>
  import { AgGridVue } from "ag-grid-vue";
  import "ag-grid-community/dist/styles/ag-grid.css";
  import "ag-grid-community/dist/styles/ag-theme-alpine.css";

  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Param from '@/config/parameters'
  import Helper from '@/handler/helper'
  import FindObject from '@/components/inc/find/findObject'
  import Funciones from '@/handler/funciones'
  import { EventBus } from '@/handler/event-bus'
  import Storage from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import {
    rawPurchaseCost,
    netPriceFromUtility,
    rawUtilityFromNet,
    netFromGross,
    grossFromNet,
    effectiveUtility,
    ceilToMultiple,
    round as pmRound,
    roundUtility as pmRoundUtility,
    roundMoney as pmRoundMoney,
  } from '@/components/inc/productPricing/pricingMath'

  export default {
    components: {
      AgGridVue,
      FindObject,
      TabHeaderActions,
    },
    data() {
      return {
        access: {
          module_id: Modules.COSTOS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'crudImportEditor',
        },
        parameters: {
          advancedImportTypes: Helper.hasParametersAccess(Param.P68),
          roundPriceUp: Helper.hasParametersAccess(Param.P69),
          salePriceCalc: Helper.hasParametersAccess(Param.P75),
          roundingMultiple: 0,
        },
        header: {
          supplier: '',
          category: '',
          subcategory: '',
        },
        mode: 'edit',
        rowIdCounter: 0,
        form: {
          suppliers_id: 0,
          suppliers: null,
          typeImport: 'default',
        },
        arr: {
          select: {
            typeImport: [
              {value:'default', text: 'Actualiza solo los productos importados'},
              {value:'warning', text: 'Marca los productos que no se importaron en la lista'},
              {value:'delete', text: 'Elimina los productos que no se importaron en la lista'},
            ],
            bulkColumns: [
              {value: 'category', text: 'Categoría'},
              {value: 'subcategory', text: 'Subcategoría'},
              {value: 'line', text: 'Línea'},
              {value: 'subline', text: 'Sublinea'},
              {value: 'price_list', text: 'Precio de lista'},
              {value: 'stock_minimum', text: 'Stock mínimo'},
              {value: 'stock_maximum', text: 'Stock máximo'},
            ],
          },
          priceLists: [],
          fiscalConditions: {},
          unitEquivalences: {},
        },
        bulkEdit: {
          column: '',
          value: '',
          mode: 'fixed',
          modeOptions: [
            { value: 'fixed', text: 'Importe fijo' },
            { value: 'percent', text: '% sobre valor actual' },
          ],
          salePriceModeOptions: [
            { value: 'sale_percent', text: '% sobre precio de venta actual' },
            { value: 'sale_multiple', text: 'Redondear a múltiplo' },
          ],
          salePriceMultiple: 100,
          salePriceMultipleOptions: [
            { value: 0, text: 'Precio entero (sin decimales)' },
            { value: 10, text: '$10' },
            { value: 50, text: '$50' },
            { value: 100, text: '$100' },
            { value: 500, text: '$500' },
            { value: 1000, text: '$1.000' },
            { value: 5000, text: '$5.000' },
          ],
          includeOptions: [
            { value: 'true', text: 'Sí' },
            { value: 'false', text: 'No' },
          ],
        },
        grid: {
          columnDefs: [],
          rowData: [],
          columns: [],
        },
        defaultColDef: {
          resizable: true,
          sortable: true,
          filter: true,
          editable: true,
          cellClassRules: {
            'cell-modified': (params) => {
              if(!params.data || !params.data._rowId) return false
              let key = params.data._rowId + '_' + params.colDef.field
              return !!this.modifiedCells[key]
            }
          },
        },
        sideBar: {
          toolPanels: ['columns'],
          defaultToolPanel: '',
        },
        gridApi: null,
        columnApi: null,
        gridHeight: '70vh',
        saving: false,
        showBulkEdit: false,
        dirty: false,
        modifiedCells: {},
        importProgress: {
          percent: 0,
          remainingSec: 0,
          timer: null,
          startedAt: null,
          durationMs: 0,
        },
        importValidation: {
          errors: [],
          warnings: [],
        },
        modal: {
          save: {
            active: false,
            title: 'Importar desde editor'
          },
          columns: {
            active: false,
            title: 'Columnas visibles'
          }
        },
        columnsVisible: {
          default: ['id', 'code', 'code_system', 'name', 'category', 'subcategory', 'default', 'price_list', 'stock_minimum', 'stock_maximum'],
          selected: [],
          options: [],
          widths: {},
        },
        virtualPriceColumns: [],
        virtualPriceColumnMap: {},
        priceWithIva: false,
      }
    },
    computed: {
      listCount() {
        const cols = this.grid.columns || []
        const refs = new Set()
        cols.forEach(key => {
          if(typeof key !== 'string') return
          if(key.startsWith('utility_')) refs.add(key.slice(8))
          if(key.startsWith('desc_')) refs.add(key.slice(5))
        })
        return refs.size
      },
      importProgressPercent() {
        return this.importProgress.percent || 0
      },
      importProgressRemainingSec() {
        return this.importProgress.remainingSec || 0
      },
      columnsVisibleGroups() {
        const optionMap = (this.columnsVisible.options || []).reduce((acc, option) => {
          acc[option.value] = option.text
          return acc
        }, {})
        const groups = []
        const generalOptions = []
        const listGroups = []
        const listGroupsMap = {}

        ;(this.grid.columnDefs || []).forEach(colDef => {
          const colId = colDef && (colDef.colId || colDef.field)
          if(!colId) return

          const listColumn = this.getListColumnGroupMeta(colId, optionMap[colId] || colDef.headerName || colId)
          if(!listColumn) {
            generalOptions.push({
              value: colId,
              text: optionMap[colId] || colDef.headerName || colId,
            })
            return
          }

          if(!listGroupsMap[listColumn.reference]) {
            listGroupsMap[listColumn.reference] = {
              key: 'list-' + listColumn.reference,
              type: 'list',
              title: listColumn.groupTitle,
              options: [],
            }
            listGroups.push(listGroupsMap[listColumn.reference])
          }

          listGroupsMap[listColumn.reference].options.push({
            value: colId,
            text: listColumn.label,
          })
        })

        if(generalOptions.length) {
          groups.push({
            key: 'general',
            type: 'general',
            title: 'Generales',
            options: generalOptions,
          })
        }

        return groups.concat(listGroups)
      },
      bulkColumnsOptions() {
        const options = []
        const seen = new Set()
        const baseMap = this.arr.select.bulkColumns.reduce((acc, opt) => {
          acc[opt.value] = opt.text
          return acc
        }, {})

        ;(this.grid.columnDefs || []).forEach(colDef => {
          const colId = colDef && (colDef.colId || colDef.field)
          if(!colId || seen.has(colId)) return

          let text = null
          if(baseMap[colId]) {
            text = baseMap[colId]
          } else if(
            colId.startsWith('include_') ||
            colId.startsWith('utility_') ||
            colId.startsWith('desc_') ||
            colId.startsWith('calc_price_sale_iva_')
          ) {
            text = colDef.headerName || colId
          }

          if(!text) return
          options.push({ value: colId, text: text })
          seen.add(colId)
        })

        this.arr.select.bulkColumns.forEach(opt => {
          if(seen.has(opt.value)) return
          options.push(opt)
          seen.add(opt.value)
        })

        return options
      },
      isBulkSalePriceColumn() {
        return !!(this.bulkEdit.column && this.bulkEdit.column.startsWith('calc_price_sale_iva_'))
      },
      isBulkIncludeColumn() {
        return !!(this.bulkEdit.column && this.bulkEdit.column.startsWith('include_'))
      },
      isBulkPercentEligible() {
        if(this.isBulkIncludeColumn) return false
        if(this.isBulkSalePriceColumn) return false
        if(!this.bulkEdit.column) return false
        return this.bulkEdit.column === 'price_list' ||
               this.bulkEdit.column.startsWith('utility_') ||
               this.bulkEdit.column.startsWith('desc_')
      },
      bulkEditPlaceholder() {
        if(this.isBulkIncludeColumn) {
          return ''
        }
        if(this.isBulkSalePriceColumn && this.bulkEdit.mode === 'sale_percent') {
          return 'Ej: 10 o -5'
        }
        if(this.isBulkPercentEligible && this.bulkEdit.mode === 'percent') {
          return 'Ej: 10 o -5'
        }
        return 'Nuevo valor'
      },
      isCreateMode() {
        return this.mode === 'create'
      },
      typeImportOptions() {
        if(this.parameters.advancedImportTypes) {
          return this.arr.select.typeImport
        }
        return this.arr.select.typeImport.filter(item => item.value === 'default')
      },
      estimatedImportMinutes() {
        const rows = this.grid.rowData ? this.grid.rowData.length : 0
        if(!rows) return 0
        const lists = Math.max(1, this.listCount || 0)
        const base = 30
        const perRow = 0.08
        const perList = 0.03
        const safety = 1.25
        const seconds = Math.ceil((base + rows * (perRow + (perList * lists))) * safety)
        return Math.ceil(seconds / 60)
      },
      conditionSuppliersActive(){
        return [
          {field: 'active', condition: true},
          {field: 'shipping', condition: false}
        ];
      },
      saveImportDescription() {
        const estimate = this.estimatedImportMinutes ? (' Tiempo estimado: ~' + this.estimatedImportMinutes + ' min.') : ''
        if(this.form.typeImport === 'delete') {
          return 'Se importarán ' + this.grid.rowData.length + ' productos. Los productos del proveedor que NO estén en este listado serán ELIMINADOS permanentemente.' + estimate
        }
        if(this.form.typeImport === 'warning') {
          return 'Se importarán ' + this.grid.rowData.length + ' productos. Los productos del proveedor que no estén en este listado serán marcados como no importados.' + estimate
        }
        return 'Se importarán ' + this.grid.rowData.length + ' productos. Solo se actualizarán los productos incluidos en este listado, el resto no se modificará.' + estimate
      },
      saveImportAlertVariant() {
        if(this.form.typeImport === 'delete') return 'danger'
        if(this.form.typeImport === 'warning') return 'warning'
        return 'info'
      },
      showRoundPriceWarning() {
        return this.parameters.roundPriceUp && this.parameters.salePriceCalc
      },
      isManualSalePriceEnabled() {
        return this.parameters.salePriceCalc && !this.parameters.roundPriceUp
      },
      hasImportValidationErrors() {
        return !!(this.importValidation.errors && this.importValidation.errors.length)
      },
      hasImportValidationWarnings() {
        return !!(this.importValidation.warnings && this.importValidation.warnings.length)
      },
    },
    watch: {
      priceWithIva() {
        this.savePriceWithIvaPreference()
        this.buildColumns()
        this.$nextTick(() => {
          this.applyColumnVisibility()
          this.refreshGridCalculations()
        })
      },
      'bulkEdit.column'(newCol) {
        if(newCol && newCol.startsWith('calc_price_sale_iva_')) {
          this.bulkEdit.mode = 'sale_percent'
          this.bulkEdit.value = ''
        } else if(newCol && newCol.startsWith('include_')) {
          this.bulkEdit.mode = 'fixed'
          this.bulkEdit.value = 'true'
        } else {
          this.bulkEdit.mode = 'fixed'
          this.bulkEdit.value = ''
        }
      },
    },
    created () {
      Helper.hasAccessView(this.access)
    },
    mounted() {
      this.loadPriceWithIvaPreference()
      this.loadPayload()
      this.loadSupportData()
    },
    activated() {
      this.loadPayload()
      this.loadSupportData()
    },
    beforeDestroy() {
      if(this.pasteHandler) {
        document.removeEventListener('paste', this.pasteHandler)
      }
      if(this.beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler)
      }
      if(this.importProgress.timer) {
        clearInterval(this.importProgress.timer)
        this.importProgress.timer = null
      }
    },
    methods: {
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      loadPayload() {
        let draft = Storage.getValue('costs_import_editor_draft')
        let payload = Storage.getValue('costs_import_editor_payload')

        if(draft) {
          try {
            draft = JSON.parse(draft)
          } catch(e) {
            draft = null
          }
        }

        if(payload) {
          payload = JSON.parse(payload)
        }

        let source = null
        this.dirty = false
        if(draft && payload) {
          if(draft.suppliers_id === payload.suppliers_id &&
             draft.category === (payload.category || '') &&
             draft.subcategory === (payload.subcategory || '')) {
            source = draft
            this.dirty = true
          } else {
            Storage.removeValue('costs_import_editor_draft')
            source = payload
          }
        } else if(payload) {
          source = payload
        }

        if(source) {
          this.mode = source.mode || 'edit'
          this.arr.priceLists = Array.isArray(source.price_lists)
            ? source.price_lists
            : ((payload && Array.isArray(payload.price_lists)) ? payload.price_lists : [])
          this.grid.columns = this.buildImportColumns(source.columns || [], source.data || [])
          this.grid.rowData = (source.data || []).map((row, idx) => {
            if(!row._rowId) row._rowId = idx + 1
            return row
          })
          this.form.suppliers_id = source.suppliers_id || 0
          this.header.supplier = source.suppliers_label || (payload ? payload.suppliers_label : '') || ''
          this.header.category = source.category || ''
          this.header.subcategory = source.subcategory || ''
          this.form.typeImport = source.typeImport || 'default'
          if(!this.parameters.advancedImportTypes && this.form.typeImport !== 'default') {
            this.form.typeImport = 'default'
          }
          this.modifiedCells = source.modifiedCells || {}
          this.rowIdCounter = this.grid.rowData.reduce((max, row) => {
            return Math.max(max, row._rowId || 0)
          }, 0)
          this.importValidation = this.validateImportSource(source)
          this.buildColumns()
          this.setupColumnsVisible()
        } else {
          this.importValidation = {
            errors: [],
            warnings: [],
          }
        }

        this.registerBeforeUnload()
      },
      getRequiredBaseColumns() {
        return [
          'id',
          'code',
          'name',
          'name_short',
          'category',
          'subcategory',
          'line',
          'subline',
          'depth',
          'long',
          'height',
          'weight',
          'unit_measurement_purchase',
          'unit_measurement_sale',
          'unit_measurement_sale_conversion',
          'iva_condition',
          'code_system',
          'barcode',
          'reference_external',
          'default',
          'price_list',
          'additional',
          'percentage_discount',
          'percentage_discount_1',
          'percentage_discount_2',
          'additional_percentage',
          'additional_percentage_1',
          'additional_1',
        ]
      },
      getListReferencesFromColumns(columns = []) {
        const refs = new Set()
        ;(columns || []).forEach(col => {
          if(typeof col !== 'string') return
          if(col.startsWith('include_')) refs.add(col.slice(8))
          if(col.startsWith('utility_')) refs.add(col.slice(8))
          if(col.startsWith('desc_')) refs.add(col.slice(5))
        })
        return Array.from(refs)
      },
      validateImportSource(source) {
        const validation = {
          errors: [],
          warnings: [],
        }

        const sourceColumns = Array.isArray(source && source.columns) ? source.columns : []
        const rows = Array.isArray(source && source.data) ? source.data : []
        const finalColumns = Array.isArray(this.grid.columns) ? this.grid.columns : []
        const requiredBaseColumns = this.getRequiredBaseColumns()

        if(source && source.columns && !Array.isArray(source.columns)) {
          validation.errors.push('La definición de columnas no llegó en formato válido.')
        }
        if(source && source.data && !Array.isArray(source.data)) {
          validation.errors.push('Las filas del importador no llegaron en formato válido.')
        }

        const missingBaseColumns = requiredBaseColumns.filter(col => !finalColumns.includes(col))
        if(missingBaseColumns.length) {
          validation.errors.push('Faltan columnas obligatorias: ' + missingBaseColumns.join(', '))
        }

        const currentBaseOrder = finalColumns.filter(col => requiredBaseColumns.includes(col))
        const expectedBaseOrder = requiredBaseColumns.filter(col => finalColumns.includes(col))
        if(currentBaseOrder.length && currentBaseOrder.join('|') !== expectedBaseOrder.join('|')) {
          validation.errors.push('El orden de las columnas base no coincide con el formato esperado del importador.')
        }

        const rawRefs = this.getListReferencesFromColumns(sourceColumns)
        rawRefs.forEach(ref => {
          const hasInclude = sourceColumns.includes(this.getIncludeField(ref))
          const hasUtility = sourceColumns.includes(this.getUtilityField(ref))
          const hasDesc = sourceColumns.includes(this.getDescField(ref))
          if(!(hasInclude && hasUtility && hasDesc)) {
            validation.warnings.push('La lista "' + ref + '" llegó incompleta. Se esperaban incluir, utilidad y descuento.')
          }
        })

        const columnRefs = this.getListReferencesFromColumns(finalColumns)
        const unknownRefs = columnRefs.filter(ref => !this.getPriceListMeta(ref))
        if(unknownRefs.length) {
          validation.warnings.push('Hay columnas de listas sin metadata asociada: ' + unknownRefs.join(', '))
        }

        const invalidIncludeRows = []
        rows.forEach((row, rowIndex) => {
          columnRefs.forEach(ref => {
            const includeField = this.getIncludeField(ref)
            if(!Object.prototype.hasOwnProperty.call(row || {}, includeField)) return
            const rawValue = row[includeField]
            if(rawValue === null || rawValue === undefined || rawValue === '') return
            if(this.normalizeIncludeValue(rawValue) === '') {
              invalidIncludeRows.push((rowIndex + 1) + ' (' + includeField + ')')
            }
          })
        })
        if(invalidIncludeRows.length) {
          const sample = invalidIncludeRows.slice(0, 5).join(', ')
          const suffix = invalidIncludeRows.length > 5 ? ' y ' + (invalidIncludeRows.length - 5) + ' más' : ''
          validation.warnings.push('Se detectaron valores inválidos en columnas incluir: ' + sample + suffix)
        }

        return validation
      },
      buildImportColumns(sourceColumns = [], rows = []) {
        const columns = []
        const seen = new Set()
        const addColumn = (col) => {
          if(!col || col === '_rowId' || seen.has(col)) return
          seen.add(col)
          columns.push(col)
        }

        sourceColumns.forEach(addColumn)
        rows.forEach(row => {
          Object.keys(row || {}).forEach(addColumn)
        })

        ;(this.arr.priceLists || []).forEach(list => {
          if(!list || !list.reference) return
          addColumn('include_' + list.reference)
          addColumn('utility_' + list.reference)
          addColumn('desc_' + list.reference)
        })

        return columns
      },
      buildColumns() {
        const readOnlyColumns = ['id']
        const defaultWidths = {
          id: 80,
          code: 125,
          name: 400,
          name_short: 220,
          category: 200,
          subcategory: 200,
        }
        const defsMap = {}
        this.grid.columns.forEach(col => {
          const isReadOnly = readOnlyColumns.includes(col)
          const includeRef = (typeof col === 'string' && col.startsWith('include_')) ? col.slice(8) : null
          const utilityRef = (typeof col === 'string' && col.startsWith('utility_')) ? col.slice(8) : null
          const descRef = (typeof col === 'string' && col.startsWith('desc_')) ? col.slice(5) : null
          const listMeta = this.getPriceListMeta(includeRef || utilityRef || descRef)
          const colDef = {
            headerName: col,
            field: col,
            colId: col,
            editable: !isReadOnly,
            minWidth: isReadOnly ? 80 : 120,
            width: defaultWidths[col] || undefined,
            cellStyle: isReadOnly ? { backgroundColor: '#f0f0f0', color: '#888' } : null,
          }
          if(col === 'price_list') {
            colDef.headerName = this.priceWithIva ? 'Precio de lista (c/IVA)' : 'Precio de lista'
            colDef.valueGetter = (params) => {
              if(!params.data) return ''
              const raw = params.data.price_list
              if(raw === null || raw === undefined || raw === '') return ''
              const net = this.parseNumeric(raw)
              if(!this.priceWithIva) return net
              const iva = this.getIvaAliquot(params.data)
              return iva > 0 ? this.roundValue(net * (1 + iva / 100), 2) : net
            }
            colDef.valueSetter = (params) => {
              const rawInput = this.parseNumeric(params.newValue)
              if(!this.priceWithIva) {
                params.data.price_list = params.newValue
                return true
              }
              const iva = this.getIvaAliquot(params.data)
              params.data.price_list = iva > 0
                ? this.roundValue(rawInput / (1 + iva / 100), 2)
                : rawInput
              return true
            }
          }
          if(includeRef) {
            colDef.headerName = listMeta && listMeta.name ? ('Incluir (' + listMeta.name + ')') : ('Incluir (' + includeRef + ')')
            colDef.width = 115
            colDef.cellStyle = () => ({
              backgroundColor: '#f7fbff',
              textAlign: 'center',
              cursor: 'pointer',
            })
            colDef.cellRenderer = (params) => {
              const checked = this.normalizeIncludeBoolean(params.value)
              return '<div style="display:flex;align-items:center;justify-content:center;height:100%;"><input type="checkbox" ' + (checked ? 'checked' : '') + ' tabindex="-1" style="pointer-events:none;" /></div>'
            }
            colDef.valueSetter = (params) => {
              params.data[col] = this.normalizeIncludeValue(params.newValue)
              return true
            }
            colDef.onCellClicked = (params) => {
              const nextValue = !this.normalizeIncludeBoolean(params.value)
              params.node.setDataValue(col, nextValue)
            }
          }
          if(utilityRef) {
            colDef.headerName = listMeta && listMeta.name ? ('Utilidad (' + listMeta.name + ')') : col
          }
          if(descRef) {
            colDef.headerName = listMeta && listMeta.name ? ('Desc. (' + listMeta.name + ')') : col
          }
          if(col.startsWith('utility_')) {
            colDef.valueFormatter = (params) => {
              return this.formatFixedNumber(params.value, 2)
            }
          }
          defsMap[col] = colDef
        })

        this.virtualPriceColumns = this.buildVirtualPriceColumns()
        this.virtualPriceColumnMap = this.virtualPriceColumns.reduce((acc, col) => {
          acc[col.colId] = col
          return acc
        }, {})

        const orderedDefs = []
        this.grid.columns.forEach(col => {
          if(typeof col === 'string' && col.startsWith('calc_price_sale_iva_')) {
            return
          }

          if(defsMap[col]) {
            orderedDefs.push(defsMap[col])
          }

          if(typeof col === 'string' && col.startsWith('desc_')) {
            const ref = col.slice(5)
            const virtualColId = this.getCalcSalePriceColumnId(ref)
            if(this.virtualPriceColumnMap[virtualColId]) {
              orderedDefs.push(this.virtualPriceColumnMap[virtualColId])
            }
          }
        })

        this.virtualPriceColumns.forEach(col => {
          if(!orderedDefs.find(def => def.colId === col.colId)) {
            orderedDefs.push(col)
          }
        })

        this.grid.columnDefs = orderedDefs
      },
      buildVirtualPriceColumns() {
        const refs = new Set()
        const cols = this.grid.columns || []
        cols.forEach(col => {
          if(typeof col !== 'string') return
          if(col.startsWith('utility_')) refs.add(col.slice(8))
        })

        return Array.from(refs).map(ref => {
          const meta = this.getPriceListMeta(ref)
          const colId = this.getCalcSalePriceColumnId(ref)
          return {
            headerName: meta && meta.name ? ('P.Venta+IVA (' + meta.name + ')') : ('P.Venta+IVA (' + ref + ')'),
            field: colId,
            colId: colId,
            editable: () => this.isManualSalePriceEnabled,
            minWidth: 165,
            width: 180,
            type: 'numericColumn',
            calcUtilityField: this.getUtilityField(ref),
            calcPriceRef: ref,
            valueGetter: (params) => this.calculateSalePriceWithIva(params.data, ref),
            valueSetter: (params) => this.applyManualSalePrice(params.data, ref, params.newValue),
            valueFormatter: (params) => this.formatCurrencyValue(params.value, meta && meta.currency ? meta.currency : 'ARS'),
            cellStyle: () => {
              if(this.isManualSalePriceEnabled) {
                return { backgroundColor: '#eefaf3', fontWeight: '600' }
              }
              return { backgroundColor: '#f5f5f5', color: '#777', fontWeight: '600' }
            },
          }
        })
      },
      setupColumnsVisible() {
        const availableIds = this.getAllColumnIds()
        this.columnsVisible.options = this.grid.columnDefs.map(col => {
          return {
            text: col.headerName || col.colId || col.field,
            value: col.colId || col.field,
          }
        })
        let stored = Storage.getValue('costs_import_editor_columns')
        if(stored) {
          try {
            stored = JSON.parse(stored)
            if(Array.isArray(stored)) {
              this.columnsVisible.selected = stored.filter(col => availableIds.includes(col))
              this.virtualPriceColumns.forEach(col => {
                if(!this.columnsVisible.selected.includes(col.colId)) {
                  this.columnsVisible.selected.push(col.colId)
                }
              })
            }
          } catch (e) {
            stored = null
          }
        }
        if(!this.columnsVisible.selected.length) {
          const defaults = new Set()
          this.columnsVisible.default.forEach(col => {
            if(this.grid.columns.includes(col)) {
              defaults.add(col)
            }
          })
          this.grid.columns.forEach(col => {
            if(typeof col === 'string' && (col.startsWith('include_') || col.startsWith('utility_'))) {
              defaults.add(col)
            }
          })
          this.virtualPriceColumns.forEach(col => {
            defaults.add(col.colId)
          })
          const selectedDefaults = Array.from(defaults)
          this.columnsVisible.selected = selectedDefaults.length ? selectedDefaults : availableIds
        }
        this.applyColumnVisibility()
      },
      getAllColumnIds() {
        const real = this.grid.columns || []
        const virtual = this.virtualPriceColumns.map(col => col.colId)
        return real.concat(virtual)
      },
      getCalcSalePriceColumnId(ref) {
        return 'calc_price_sale_iva_' + ref
      },
      getIncludeField(ref) {
        return 'include_' + ref
      },
      getUtilityField(ref) {
        return 'utility_' + ref
      },
      getDescField(ref) {
        return 'desc_' + ref
      },
      getPriceListMeta(ref) {
        return (this.arr.priceLists || []).find(list => list.reference === ref) || null
      },
      getListColumnGroupMeta(colId, fallbackText = '') {
        if(!colId || typeof colId !== 'string') return null

        let reference = null
        let label = fallbackText
        if(colId.startsWith('include_')) {
          reference = colId.slice(8)
          label = 'Incluir'
        } else if(colId.startsWith('utility_')) {
          reference = colId.slice(8)
          label = 'Utilidad'
        } else if(colId.startsWith('desc_')) {
          reference = colId.slice(5)
          label = 'Descuento'
        } else if(colId.startsWith('calc_price_sale_iva_')) {
          reference = colId.slice('calc_price_sale_iva_'.length)
          label = 'Precio venta'
        }

        if(!reference) return null

        const meta = this.getPriceListMeta(reference)
        return {
          reference: reference,
          label: label,
          groupTitle: meta && meta.name ? meta.name : reference,
        }
      },
      getDirtyFieldFromColDef(colDef) {
        return colDef && colDef.calcUtilityField ? colDef.calcUtilityField : (colDef.field || colDef.colId)
      },
      getDirtyFieldByColId(colId) {
        const virtual = this.virtualPriceColumnMap[colId]
        if(virtual && virtual.calcUtilityField) {
          return virtual.calcUtilityField
        }
        return colId
      },
      loadPriceWithIvaPreference() {
        try {
          var stored = localStorage.getItem('bcsp_prefs')
          var prefs = stored ? JSON.parse(stored) : {}
          this.priceWithIva = !!prefs.priceWithIva
        } catch(e) { /* La preferencia local es opcional. */ }
      },
      savePriceWithIvaPreference() {
        try {
          var stored = localStorage.getItem('bcsp_prefs')
          var prefs = stored ? JSON.parse(stored) : {}
          prefs.priceWithIva = this.priceWithIva
          localStorage.setItem('bcsp_prefs', JSON.stringify(prefs))
        } catch(e) { /* La preferencia local es opcional. */ }
      },
      loadSupportData() {
        this.loadFiscalConditions()
        this.loadUnitMeasurements()
        this.loadRoundingConfig()
      },
      loadFiscalConditions() {
        var result = serviceAPI.obtenerCondicionesIva()
        result.then((response) => {
          const map = {}
          ;(response.data || []).forEach(item => {
            map[item.id] = this.parseNumeric(item.aliquot)
          })
          this.arr.fiscalConditions = map
          this.refreshGridCalculations()
        })
        .catch(() => {})
      },
      loadUnitMeasurements() {
        var result = serviceAPI.obtenerUnidadesMedida()
        result.then((response) => {
          const equivalences = {}
          ;(response.data || []).forEach(unit => {
            const inputRef = unit && unit.reference ? unit.reference : ''
            ;((unit && unit.equivalences) ? unit.equivalences : []).forEach(eq => {
              const inRef = eq && eq.unit_measurement_input && eq.unit_measurement_input.reference
                ? eq.unit_measurement_input.reference
                : inputRef
              const outRef = eq && eq.unit_measurement_result && eq.unit_measurement_result.reference
                ? eq.unit_measurement_result.reference
                : ''
              if(!inRef || !outRef) return
              equivalences[inRef + '|' + outRef] = {
                operation: eq.operation || '',
                variable: eq.variable || '',
                value: this.parseNumeric(eq.value),
                surcharge: this.parseNumeric(eq.surcharge),
              }
            })
          })
          this.arr.unitEquivalences = equivalences
          this.refreshGridCalculations()
        })
        .catch(() => {})
      },
      loadRoundingConfig() {
        if(!this.parameters.roundPriceUp) {
          this.parameters.roundingMultiple = 0
          return
        }
        serviceAPI.obtenerConfiguracion().then(response => {
          const cfg = (response.data || []).find(item => item.id == 1)
          this.parameters.roundingMultiple = cfg ? parseInt(cfg.value_number) || 0 : 0
          this.refreshGridCalculations()
        })
        .catch(() => {})
      },
      refreshGridCalculations() {
        if(this.gridApi) {
          this.$nextTick(() => {
            this.gridApi.refreshCells({ force: true })
          })
        }
      },
      parseNumeric(value) {
        if(value === null || value === undefined || value === '') {
          return 0
        }
        if(typeof value === 'number') {
          return isNaN(value) ? 0 : value
        }
        let str = String(value).trim()
        if(!str) {
          return 0
        }
        str = str.replace(/\s/g, '')
        if(str.includes(',') && str.includes('.')) {
          str = str.replace(/\./g, '').replace(',', '.')
        } else if(str.includes(',')) {
          str = str.replace(',', '.')
        }
        let num = parseFloat(str)
        return isNaN(num) ? 0 : num
      },
      roundValue(value, digits = 2) {
        return pmRound(this.parseNumeric(value), digits)
      },
      formatFixedNumber(value, digits = 2) {
        if(value === null || value === undefined || value === '') {
          return ''
        }
        const amount = this.parseNumeric(value)
        return amount.toFixed(digits)
      },
      normalizeIncludeValue(value) {
        if(value === null || value === undefined) {
          return ''
        }
        if(typeof value === 'boolean') {
          return value ? 'true' : 'false'
        }
        const str = String(value).trim().toLowerCase()
        if(!str) {
          return ''
        }
        if(['1', 'true', 'si', 'sí', 'yes', 'y', 'x'].includes(str)) {
          return 'true'
        }
        if(['0', 'false', 'no', 'n'].includes(str)) {
          return 'false'
        }
        return ''
      },
      normalizeIncludeBoolean(value) {
        return this.normalizeIncludeValue(value) === 'true'
      },
      formatIncludeValue(value) {
        const normalized = this.normalizeIncludeValue(value)
        if(normalized === 'true') return 'SI'
        if(normalized === 'false') return 'NO'
        return ''
      },
      formatCurrencyValue(value, currency = 'ARS') {
        const amount = this.parseNumeric(value)
        return Intl.NumberFormat('es-AR', {
          style: 'currency',
          currency: currency || 'ARS',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount)
      },
      calculateCostPrice(row) {
        if(!row) return 0
        const price = rawPurchaseCost({
          priceList: this.parseNumeric(row.price_list),
          additional: this.parseNumeric(row.additional),
          percentageDiscount: this.parseNumeric(row.percentage_discount),
          percentageDiscount1: this.parseNumeric(row.percentage_discount_1),
          percentageDiscount2: this.parseNumeric(row.percentage_discount_2),
          additionalPercentage: this.parseNumeric(row.additional_percentage),
          additionalPercentage1: this.parseNumeric(row.additional_percentage_1),
          additional1: this.parseNumeric(row.additional_1),
        })

        if(this.parameters.roundPriceUp) {
          return ceilToMultiple(price, this.parameters.roundingMultiple)
        }
        return this.roundValue(price, 2)
      },
      getEffectiveUtility(row, ref) {
        const utility = this.parseNumeric(row ? row[this.getUtilityField(ref)] : 0)
        const meta = this.getPriceListMeta(ref)
        const defaultUtility = meta ? this.parseNumeric(meta.utility) : 0
        return effectiveUtility({ utility, defaultUtility })
      },
      getListDiscount(row, ref) {
        return this.parseNumeric(row ? row[this.getDescField(ref)] : 0)
      },
      getIvaAliquot(row) {
        if(!row) return 0
        const ivaId = this.parseNumeric(row.iva_condition)
        return this.arr.fiscalConditions[ivaId] || 0
      },
      getEquivalence(inputRef, resultRef) {
        if(!inputRef || !resultRef) return null
        return this.arr.unitEquivalences[inputRef + '|' + resultRef] || null
      },
      applyUnitEquivalence(price, inputRef, resultRef, conversion) {
        let result = this.parseNumeric(price)
        if(!inputRef || !resultRef || inputRef === resultRef) {
          return result
        }

        const equiv = this.getEquivalence(inputRef, resultRef)
        if(!equiv) {
          return 0
        }

        let value = this.parseNumeric(equiv.value)
        if(equiv.operation === 'multiplicacion_variable' || equiv.operation === 'division_variable') {
          value = this.parseNumeric(conversion)
        }

        if(equiv.operation === 'multiplicacion_variable' || equiv.operation === 'multiplicacion_fijo') {
          if(result > 0) {
            if(!equiv.variable) {
              result = result * value
            } else if(equiv.variable === 'cantidad') {
              result = value > 1 ? (result * value) : (result / value)
            } else if(equiv.variable === 'densidad') {
              result = value > 1 ? (result / value) : (result * value)
            }
          } else {
            result = 0
          }
        }

        if(equiv.operation === 'division_variable' || equiv.operation === 'division_fijo') {
          if(value > 0) {
            if(!equiv.variable) {
              result = result / value
            } else if(equiv.variable === 'cantidad') {
              result = value > 1 ? (result / value) : (result * value)
            } else if(equiv.variable === 'densidad') {
              result = value > 1 ? (result * value) : (result / value)
            }
          } else {
            result = 0
          }
        }

        if(this.parseNumeric(equiv.surcharge)) {
          result += (result * this.parseNumeric(equiv.surcharge)) / 100
        }

        return result
      },
      calculateSalePriceWithoutIva(row, ref) {
        if(!row) return 0
        const cost = this.calculateCostPrice(row)
        const utility = this.getEffectiveUtility(row, ref)
        const desc = this.getListDiscount(row, ref)
        let amount = netPriceFromUtility({ cost, utility, discount: desc })

        amount = this.applyUnitEquivalence(
          amount,
          row.unit_measurement_purchase,
          row.unit_measurement_sale,
          row.unit_measurement_sale_conversion
        )

        if(this.parameters.roundPriceUp) {
          return ceilToMultiple(amount, this.parameters.roundingMultiple)
        }

        return amount
      },
      calculateSalePriceWithIva(row, ref) {
        if(!row) return 0
        const amountNet = this.calculateSalePriceWithoutIva(row, ref)
        const iva = this.getIvaAliquot(row)
        if(!iva) return this.roundValue(amountNet, 2)
        if(!this.parameters.roundPriceUp) {
          return pmRoundMoney(grossFromNet(amountNet, iva))
        }
        const amountNetRounded = this.roundValue(amountNet, 2)
        return pmRoundMoney(grossFromNet(amountNetRounded, iva))
      },
      applyManualSalePrice(row, ref, rawValue) {
        if(!row || !this.isManualSalePriceEnabled) {
          return false
        }

        const targetWithIva = this.parseNumeric(rawValue)
        const utilityField = this.getUtilityField(ref)
        if(!utilityField || !this.grid.columns.includes(utilityField)) {
          return false
        }

        const cost = this.calculateCostPrice(row)
        if(cost <= 0) {
          row[utilityField] = 0
          return true
        }

        const iva = this.getIvaAliquot(row)
        // Con P75 activo el neto puede requerir mas de 2 decimales para que
        // el total final con IVA coincida exactamente despues del redondeo.
        let targetNet = iva > 0 ? netFromGross(targetWithIva, iva) : targetWithIva

        targetNet = this.applyUnitEquivalence(
          targetNet,
          row.unit_measurement_sale,
          row.unit_measurement_purchase,
          row.unit_measurement_sale_conversion
        )

        const desc = this.getListDiscount(row, ref)
        const discountBase = 1 - desc / 100
        if(desc && discountBase <= 0) {
          return false
        }

        const utility = rawUtilityFromNet({ cost, netPrice: targetNet, discount: desc })
        row[utilityField] = pmRoundUtility(utility, 10)
        return true
      },
      onGridReady(params) {
        this.gridApi = params.api
        this.columnApi = params.columnApi
        this.registerPasteListener()
        this.applyColumnVisibility()
        this.loadColumnWidths()
        if(Object.keys(this.modifiedCells).length) {
          this.$nextTick(() => { this.gridApi.refreshCells({ force: true }) })
        }
      },
      onCellValueChanged(event) {
        this.dirty = true
        if(event && event.data && event.data._rowId && event.colDef) {
          let field = this.getDirtyFieldFromColDef(event.colDef)
          let key = event.data._rowId + '_' + field
          this.$set(this.modifiedCells, key, true)
          this.$nextTick(() => {
            this.gridApi.refreshCells({ rowNodes: [event.node], force: true })
          })
        }
        this.saveDraft()
      },
      onColumnResized(event) {
        if(event && event.finished) {
          this.saveColumnWidths()
        }
      },
      openColumnsModal() {
        this.modal.columns.active = true
      },
      applyColumnVisibility() {
        if(!this.columnApi) {
          return
        }
        this.getAllColumnIds().forEach(colId => {
          if(this.columnApi.getColumn(colId)) {
            this.columnApi.setColumnVisible(colId, this.columnsVisible.selected.includes(colId))
          }
        })
        Storage.setValue('costs_import_editor_columns', JSON.stringify(this.columnsVisible.selected))
        this.modal.columns.active = false
      },
      loadColumnWidths() {
        let stored = Storage.getValue('costs_import_editor_columns_widths')
        if(stored) {
          try {
            stored = JSON.parse(stored)
            if(stored && typeof stored === 'object') {
              this.columnsVisible.widths = stored
              Object.keys(stored).forEach(colId => {
                if(this.columnApi.getColumn(colId)) {
                  this.columnApi.setColumnWidth(colId, stored[colId], false)
                }
              })
            }
          } catch (e) {
            // ignore
          }
        }
      },
      saveColumnWidths() {
        if(!this.columnApi) {
          return
        }
        let state = this.columnApi.getColumnState()
        let widths = {}
        state.forEach(col => {
          if(col && col.colId && col.width) {
            widths[col.colId] = col.width
          }
        })
        this.columnsVisible.widths = widths
        Storage.setValue('costs_import_editor_columns_widths', JSON.stringify(widths))
      },
      registerPasteListener() {
        if(this.pasteHandler) {
          return
        }
        this.pasteHandler = (event) => {
          this.handlePaste(event)
        }
        document.addEventListener('paste', this.pasteHandler)
      },
      handlePaste(event) {
        if(!this.gridApi || !this.columnApi) {
          return
        }
        const focused = this.gridApi.getFocusedCell()
        if(!focused) {
          return
        }
        const text = event.clipboardData ? event.clipboardData.getData('text/plain') : ''
        if(!text) {
          return
        }

        event.preventDefault()
        const rows = text.replace(/\r\n$/,'').split(/\r\n|\n|\r/)
        const cols = this.columnApi.getAllDisplayedColumns()
        const startColIndex = cols.findIndex(col => col.getColId() === focused.column.getColId())
        if(startColIndex < 0) {
          return
        }

        rows.forEach((rowText, rIdx) => {
          const rowNode = this.gridApi.getDisplayedRowAtIndex(focused.rowIndex + rIdx)
          if(!rowNode) {
            return
          }
          const values = rowText.split('\t')
          values.forEach((value, cIdx) => {
            const col = cols[startColIndex + cIdx]
            if(!col) {
              return
            }
            const colDef = col.getColDef ? col.getColDef() : null
            if(colDef) {
              const editable = typeof colDef.editable === 'function'
                ? colDef.editable({ data: rowNode.data, colDef: colDef })
                : colDef.editable
              if(editable === false) {
                return
              }
            }
            rowNode.setDataValue(col.getColId(), value)
            if(rowNode.data && rowNode.data._rowId) {
              let field = this.getDirtyFieldByColId(col.getColId())
              this.$set(this.modifiedCells, rowNode.data._rowId + '_' + field, true)
            }
          })
        })
        this.dirty = true
        this.saveDraft()
      },
      applyBulkEdit() {
        if(!this.gridApi || !this.bulkEdit.column) {
          return
        }

        if(this.isBulkIncludeColumn) {
          const nextIncludeValue = this.normalizeIncludeValue(this.bulkEdit.value)
          this.gridApi.forEachNodeAfterFilter((node) => {
            node.setDataValue(this.bulkEdit.column, nextIncludeValue)
            if(node.data && node.data._rowId) {
              this.$set(this.modifiedCells, node.data._rowId + '_' + this.bulkEdit.column, true)
            }
          })
          this.gridApi.refreshCells({ columns: [this.bulkEdit.column], force: true })
          this.dirty = true
          this.saveDraft()
          return
        }

        if(this.isBulkSalePriceColumn) {
          const ref = this.bulkEdit.column.slice('calc_price_sale_iva_'.length)
          const utilityField = this.getUtilityField(ref)
          if(!utilityField || !this.grid.columns.includes(utilityField)) return

          this.gridApi.forEachNodeAfterFilter((node) => {
            if(!node.data) return
            const currentPrice = this.calculateSalePriceWithIva(node.data, ref)
            let targetPrice = currentPrice

            if(this.bulkEdit.mode === 'sale_percent') {
              const pct = parseFloat(String(this.bulkEdit.value).replace(',', '.')) || 0
              targetPrice = currentPrice * (1 + pct / 100)
            } else if(this.bulkEdit.mode === 'sale_multiple') {
              const multiple = this.bulkEdit.salePriceMultiple
              if(multiple > 0) {
                targetPrice = Math.ceil(currentPrice / multiple) * multiple
              } else {
                targetPrice = Math.round(currentPrice)
              }
            }

            if(this.applyManualSalePrice(node.data, ref, targetPrice)) {
              if(node.data._rowId) {
                this.$set(this.modifiedCells, node.data._rowId + '_' + utilityField, true)
              }
            }
          })

          this.gridApi.refreshCells({ force: true })
          this.dirty = true
          this.saveDraft()
          return
        }

        this.gridApi.forEachNodeAfterFilter((node) => {
          let nextValue = this.bulkEdit.value
          if(this.isBulkPercentEligible) {
            let currentRaw = node.data ? node.data[this.bulkEdit.column] : 0
            if(this.bulkEdit.column === 'price_list' && this.priceWithIva && this.bulkEdit.mode === 'percent') {
              const net = this.parseNumeric(currentRaw)
              const iva = this.getIvaAliquot(node.data)
              currentRaw = iva > 0 ? this.roundValue(net * (1 + iva / 100), 2) : net
            }
            const current = parseFloat(String(currentRaw).replace(',', '.')) || 0
            const input = parseFloat(String(this.bulkEdit.value).replace(',', '.')) || 0
            if(this.bulkEdit.mode === 'percent') {
              nextValue = parseFloat((current * (1 + (input / 100))).toFixed(2))
            } else {
              nextValue = input
            }
          }
          node.setDataValue(this.bulkEdit.column, nextValue)
          if(node.data && node.data._rowId) {
            this.$set(this.modifiedCells, node.data._rowId + '_' + this.bulkEdit.column, true)
          }
        })
        this.dirty = true
        this.saveDraft()
      },
      addRow() {
        if(!this.grid.columns || !this.grid.columns.length) {
          return
        }
        const row = {}
        this.grid.columns.forEach(col => {
          row[col] = col.startsWith('include_') ? 'false' : ''
        })
        if(this.grid.columns.includes('category') && this.header.category) {
          row.category = this.header.category
        }
        if(this.grid.columns.includes('subcategory') && this.header.subcategory) {
          row.subcategory = this.header.subcategory
        }
        this.rowIdCounter += 1
        row._rowId = this.rowIdCounter
        this.grid.rowData.push(row)
        if(this.gridApi) {
          this.gridApi.setRowData(this.grid.rowData)
        }
        this.dirty = true
        this.saveDraft()
      },
      removeFocusedRow() {
        if(!this.gridApi) {
          return
        }
        const focused = this.gridApi.getFocusedCell()
        if(!focused) {
          this.$awn.warning("Seleccione una fila para eliminar.");
          return
        }
        const rowIndex = focused.rowIndex
        if(rowIndex == null || rowIndex < 0 || rowIndex >= this.grid.rowData.length) {
          this.$awn.warning("Seleccione una fila válida para eliminar.");
          return
        }
        const row = this.grid.rowData[rowIndex] || {}
        const hasId = row.id !== undefined && row.id !== null && row.id !== ''
        const hasCode = row.code !== undefined && row.code !== null && row.code !== ''
        const hasName = row.name !== undefined && row.name !== null && row.name !== ''
        let label = ''
        if(hasId || hasCode || hasName) {
          const parts = []
          if(hasId) parts.push('id: ' + row.id)
          if(hasCode) parts.push('code: ' + row.code)
          if(hasName) parts.push('name: ' + row.name)
          label = ' (' + parts.join(' / ') + ')'
        } else {
          label = ' (registro #' + (rowIndex + 1) + ')'
        }
        this.confirmDialog('¿Eliminar el item seleccionado' + label + '?', {
          title: 'Confirmar eliminación',
          icon: 'delete',
          danger: true,
          okTitle: 'Eliminar',
        })
        .then(value => {
          if(!value) return
          this.grid.rowData.splice(rowIndex, 1)
          this.gridApi.setRowData(this.grid.rowData)
          this.dirty = true
          this.saveDraft()
        })
      },
      saveDraft() {
        let rows = this.getGridData()
        let draft = {
          columns: this.grid.columns,
          data: rows,
          suppliers_id: this.form.suppliers_id,
          suppliers_label: this.header.supplier,
          price_lists: this.arr.priceLists,
          category: this.header.category || '',
          subcategory: this.header.subcategory || '',
          typeImport: this.form.typeImport,
          modifiedCells: this.modifiedCells,
          mode: this.mode,
        }
        Storage.setValue('costs_import_editor_draft', JSON.stringify(draft))
      },
      clearDraft() {
        Storage.removeValue('costs_import_editor_draft')
      },
      resetToOriginal() {
        this.confirmDialog('¿Descartar todos los cambios y volver a los datos originales?', {
          title: 'Restaurar original',
          icon: 'undo',
          danger: true,
          okTitle: 'Sí, restaurar',
        }).then(value => {
          if(!value) return
          this.clearDraft()
          this.loadPayload()
        })
      },
      registerBeforeUnload() {
        if(this.beforeUnloadHandler) {
          return
        }
        this.beforeUnloadHandler = (e) => {
          if(this.dirty) {
            e.preventDefault()
            e.returnValue = ''
          }
        }
        window.addEventListener('beforeunload', this.beforeUnloadHandler)
      },
      loadSuppliers(object) {
        if(object){
          this.form.suppliers = object
          this.form.suppliers_id = object.id
          this.header.supplier = object.name || ''
        } else {
          this.form.suppliers = null
          this.form.suppliers_id = 0
          this.header.supplier = ''
        }
      },
      getGridData() {
        let rows = []
        if(this.gridApi) {
          this.gridApi.forEachNode(node => {
            rows.push(node.data)
          })
        } else {
          rows = this.grid.rowData
        }
        return rows
      },
      getOrderedRows() {
        let rows = this.getGridData()
        return rows.map(row => {
          let ordered = {}
          this.grid.columns.forEach(key => {
            ordered[key] = row[key] === null || row[key] === undefined ? '' : row[key]
          })
          return ordered
        })
      },
      buildCsvString() {
        let csv = ''
        csv += Funciones.objectToCSVRow(this.grid.columns)

        this.getOrderedRows().forEach(row => {
          let line = this.grid.columns.map(key => row[key])
          csv += Funciones.objectToCSVRow(line)
        })
        return csv
      },
      downloadCSV() {
        Funciones.exportToCSV(this.getOrderedRows(), 'list-cost-products')
      },
      startImportProgress() {
        if(this.importProgress.timer) {
          clearInterval(this.importProgress.timer)
        }
        const minutes = this.estimatedImportMinutes || 0
        const durationMs = minutes > 0 ? minutes * 60 * 1000 : 60000
        this.importProgress.durationMs = durationMs
        this.importProgress.startedAt = Date.now()
        this.importProgress.percent = 0
        this.importProgress.remainingSec = Math.ceil(durationMs / 1000)

        this.importProgress.timer = setInterval(() => {
          const elapsed = Date.now() - this.importProgress.startedAt
          const ratio = Math.min(1, elapsed / this.importProgress.durationMs)
          this.importProgress.percent = Math.round(ratio * 100)
          const remaining = Math.max(0, this.importProgress.durationMs - elapsed)
          this.importProgress.remainingSec = Math.ceil(remaining / 1000)
          if(ratio >= 1) {
            this.importProgress.percent = 100
          }
        }, 1000)
      },
      stopImportProgress() {
        if(this.importProgress.timer) {
          clearInterval(this.importProgress.timer)
          this.importProgress.timer = null
        }
        this.importProgress.percent = 0
        this.importProgress.remainingSec = 0
        this.importProgress.startedAt = null
        this.importProgress.durationMs = 0
      },
      formatDuration(seconds) {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return mins + 'm ' + secs.toString().padStart(2, '0') + 's'
      },
      goBack() {
        if(!this.dirty) {
          this.clearDraft()
          this.$router.go(-1)
          return
        }
        this.confirmDialog('Tiene cambios sin guardar. Si vuelve, se perderán las modificaciones realizadas. ¿Desea continuar?', {
          title: 'Cambios sin guardar',
          danger: true,
          okTitle: 'Sí, volver sin guardar',
          cancelTitle: 'Seguir editando',
        })
        .then(value => {
          if(value) {
            this.dirty = false
            this.clearDraft()
            this.$router.go(-1)
          }
        })
      },
      openSaveModal() {
        if(this.hasImportValidationErrors) {
          this.$awn.warning("El archivo tiene inconsistencias de formato. Revise las alertas antes de guardar.")
          return
        }
        this.modal.save.active = true
      },
      confirmAndSave() {
        if(!this.form.suppliers_id) {
          this.$awn.warning("Debe seleccionar un proveedor")
          return
        }
        if(this.hasImportValidationErrors) {
          this.$awn.warning("El archivo tiene inconsistencias de formato. Revise las alertas antes de guardar.")
          return
        }
        this.modal.save.active = false

        let confirmTitle = 'Confirmar importación'
        let confirmVariant = 'dark'
        let supplierLabel = this.header.supplier || 'el proveedor seleccionado'
        let confirmMessage = '¿Está seguro de importar ' + this.grid.rowData.length + ' productos para ' + supplierLabel + '?'
        if(this.estimatedImportMinutes) {
          confirmMessage += '\nTiempo estimado: ~' + this.estimatedImportMinutes + ' min.'
        }

        if(this.form.typeImport === 'delete') {
          confirmTitle = 'Atención: importación con eliminación'
          confirmVariant = 'danger'
          confirmMessage = 'Esta acción ELIMINARÁ los productos que no estén en el listado. ¿Desea continuar?'
        }

        this.confirmDialog(confirmMessage, {
          title: confirmTitle,
          icon: confirmVariant === 'danger' ? 'delete' : 'save',
          danger: confirmVariant === 'danger',
          okTitle: 'Sí, importar',
        })
        .then(value => {
          if(value) {
            this.saveImport()
          }
        })
      },
      saveImport() {
        if(!this.form.suppliers_id) {
          this.$awn.alert("Debe seleccionar un proveedor")
          return
        }

        this.saving = true
        this.startImportProgress()
        let loader = this.$loading.show()
        let csvString = this.buildCsvString()
        let blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
        let file = new File([blob], 'list-cost-products.csv', { type: 'text/csv' })

        let fileForm = new FormData()
        fileForm.append('file', file)

        let payload = {
          suppliers_id: this.form.suppliers_id,
          typeImport: this.form.typeImport,
          file: true,
        }

        var result = serviceAPI.importarSuppliersProductosPrices(JSON.stringify(payload), fileForm)

        result.then(() => {
          this.saving = false
          this.stopImportProgress()
          loader.hide()
          this.dirty = false
          this.clearDraft()
          this.$awn.success("Archivo importado con éxito")

          EventBus.$emit('Tabs:Close', {
            fullPath: this.$route.fullPath,
            goTo: {
              name: 'CostsStaffPriceListSuppliers',
              params: {
                widget: {
                  suppliers_id: this.form.suppliers_id,
                  suppliers_label: this.header.supplier || '',
                  category: this.header.category || '',
                  subcategory: this.header.subcategory || '',
                }
              }
            }
          })
        })
        .catch(error => {
          this.saving = false
          this.stopImportProgress()
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
    }
  }
</script>

<style>
  .my-grid-custom .ag-cell-value {
    align-content: space-around;
    line-height: normal;
  }
  .my-grid-custom .cell-modified {
    background-color: #fff9e6 !important;
    border-left: 2px solid #f0c040 !important;
  }
  .cpls-mode-indicators {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
  }
  @media (max-width: 767.98px) {
    .cpls-mode-indicators {
      justify-content: flex-start;
    }
  }
  .cpls-iva-toggle {
    align-items: center;
    background: var(--app-color-surface-subtle);
    border: 1px solid var(--app-color-border);
    border-radius: 6px;
    display: inline-flex;
    height: 27px;
    padding: 0 10px;
    transition: background-color 0.15s, border-color 0.15s;
  }
  .cpls-iva-toggle .custom-control {
    line-height: 1.3;
  }
  .cpls-iva-toggle--active {
    background: var(--app-color-primary-soft);
    border-color: var(--app-color-primary-200);
  }
  @media (max-width: 767.98px) {
    .cpls-iva-toggle {
      height: 34px;
    }
  }
  .cpls-rounded-badge {
    align-items: center;
    background: var(--app-color-warning-soft);
    border: 1px solid var(--app-color-warning-border);
    border-radius: 999px;
    color: var(--app-color-warning-hover);
    cursor: default;
    display: inline-flex;
    flex-shrink: 0;
    font-size: var(--app-text-xs);
    font-weight: var(--app-font-semibold);
    height: 27px;
    padding: 0 10px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
  @media (max-width: 767.98px) {
    .cpls-rounded-badge {
      height: 34px;
    }
  }
  .cie-rounded-badge--clickable {
    cursor: pointer;
  }
</style>
